import { Injectable } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, of, switchMap } from 'rxjs';

import {
  BasketDiscountCreateRequest,
  BasketDiscountResponse,
  PageDiscountResponse,
} from '@app/api/models';
import { DiscountsService } from '@app/api/services';
import { SnackbarComponent } from '@shared/components/snackbar/snackbar.component';
import { paramConvert } from '@shared/helpers/service-helpers';

import { BasketDiscountsActions } from './basket-discounts.slice';

@Injectable({ providedIn: 'root' })
export class BasketDiscountsEffects {
  loadDiscounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BasketDiscountsActions.loadDiscounts.trigger),
      switchMap((_action) =>
        this.discountsService
          .getDiscountsList({ discountType: 'BSK', page: 0, size: 9999 })
          .pipe(
            map((response: PageDiscountResponse) =>
              BasketDiscountsActions.loadDiscounts.success({
                response: response.content as Array<BasketDiscountResponse>,
              })
            ),
            catchError(() => of(BasketDiscountsActions.loadDiscounts.failure()))
          )
      )
    )
  );

  saveDiscounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BasketDiscountsActions.saveDiscounts.trigger),
      switchMap((action) => {
        const requestBody = action.request.discounts.map(
          (request: BasketDiscountCreateRequest) => ({
            ...paramConvert(request),
            fromSiteId: action.request.fromSiteId,
            siteMaxOrderValue:
              request.siteMaxOrderValue === null
                ? 99999999
                : request.siteMaxOrderValue,
            siteMinOrderValue:
              request.siteMinOrderValue === null
                ? 0
                : request.siteMinOrderValue,
          })
        );
        return this.discountsService
          .createOrUpdateDiscounts({ body: requestBody })
          .pipe(
            map((response: Array<BasketDiscountResponse>) => {
              this.snackBar.openFromComponent(SnackbarComponent, {
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
                duration: 3000,
                data: this.translateService.instant('SHARED.SAVE_SUCCESS'),
              });
              this.store.dispatch(
                BasketDiscountsActions.loadDiscounts.trigger()
              );
              return BasketDiscountsActions.saveDiscounts.success({ response });
            }),
            catchError(() => of(BasketDiscountsActions.saveDiscounts.failure()))
          );
      })
    )
  );

  deleteDiscounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BasketDiscountsActions.deleteDiscounts.trigger),
      switchMap((action) => {
        return this.discountsService
          .deleteDiscounts({ body: { discountIdList: action.request } })
          .pipe(
            map(() => {
              this.snackBar.openFromComponent(SnackbarComponent, {
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
                duration: 3000,
                data: this.translateService.instant('SHARED.DELETE_SUCCESS'),
              });
              return BasketDiscountsActions.deleteDiscounts.success();
            }),
            catchError(() =>
              of(BasketDiscountsActions.deleteDiscounts.failure())
            )
          );
      })
    )
  );

  constructor(
    private readonly actions$: Actions,
    private discountsService: DiscountsService,
    private translateService: TranslateService,
    private readonly snackBar: MatSnackBar,
    private readonly store: Store
  ) {}
}
